import React, { useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Form, Modal, Row, ToggleButton } from "react-bootstrap";
import { apiSendJson, TEST_TYPES } from "./contentApi";

/** Create (test = null) or edit a test. `defaults` pre-fills level and type when creating. */
export const TestFormModal = ({ show, test, defaults, levels, onClose, onSaved }) => {
    const [levelId, setLevelId] = useState("");
    const [type, setType] = useState(TEST_TYPES[0].value);
    const [title, setTitle] = useState("");
    const [screenOrder, setScreenOrder] = useState("");
    const [isPremium, setIsPremium] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const isEdit = Boolean(test);

    useEffect(() => {
        if (!show) return;
        setLevelId(String(test?.levelId ?? defaults.levelId ?? levels[0]?.id ?? ""));
        setType(test?.testType ?? defaults.type ?? TEST_TYPES[0].value);
        setTitle(test?.title ?? "");
        setScreenOrder(test?.screenOrder != null ? String(test.screenOrder) : "");
        setIsPremium(Boolean(test?.isPremium));
        setSaving(false);
        setError("");
    }, [show, test, defaults, levels]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!title.trim()) return setError("Please enter a title.");

        const body = {
            levelId: Number(levelId),
            type,
            title: title.trim(),
            screenOrder: screenOrder === "" ? null : Number(screenOrder),
            isPremium,
        };

        try {
            setSaving(true);
            setError("");
            const saved = isEdit
                ? await apiSendJson("PUT", `/admin/content/tests/${test.id}`, body)
                : await apiSendJson("POST", "/admin/content/tests", body);
            onSaved(saved);
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title className="fs-5">{isEdit ? "Edit test" : "Add test"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="g-2 mb-3">
                        <Col>
                            <Form.Label className="small text-muted mb-1">Level</Form.Label>
                            <Form.Select value={levelId} onChange={(e) => setLevelId(e.target.value)} disabled={saving || isEdit}>
                                {levels.map((level) => (
                                    <option key={level.id} value={level.id}>{level.title}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col>
                            <Form.Label className="small text-muted mb-1">Type</Form.Label>
                            <Form.Select value={type} onChange={(e) => setType(e.target.value)} disabled={saving || isEdit}>
                                {TEST_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    <Row className="g-2 mb-3">
                        <Col xs={8}>
                            <Form.Label className="small text-muted mb-1">Title</Form.Label>
                            <Form.Control
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. UNIT 1"
                                disabled={saving}
                            />
                        </Col>
                        <Col xs={4}>
                            <Form.Label className="small text-muted mb-1">Order (unit)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                placeholder={isEdit ? "" : "Auto (last)"}
                                value={screenOrder}
                                onChange={(e) => setScreenOrder(e.target.value)}
                                disabled={saving}
                            />
                        </Col>
                    </Row>

                    <Form.Check
                        type="switch"
                        id="test-is-premium"
                        label="Premium test"
                        checked={isPremium}
                        onChange={(e) => setIsPremium(e.target.checked)}
                        disabled={saving}
                    />
                    {isEdit && (
                        <Form.Text muted>Level and type can't be changed after a test is created.</Form.Text>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving}>{isEdit ? "Save changes" : "Add test"}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const OPTIONS = [
    { letter: "a", label: "A", field: "optionA" },
    { letter: "b", label: "B", field: "optionB" },
    { letter: "c", label: "C", field: "optionC" },
];

/** Create (question = null) or edit a question of `testId`, including its correct answer. */
export const QuestionFormModal = ({ show, testId, question, onClose, onSaved }) => {
    const [values, setValues] = useState({ question: "", optionA: "", optionB: "", optionC: "" });
    const [correctOption, setCorrectOption] = useState("");
    const [screenOrder, setScreenOrder] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const isEdit = Boolean(question);

    useEffect(() => {
        if (!show) return;
        setValues({
            question: question?.question ?? "",
            optionA: question?.optionA ?? "",
            optionB: question?.optionB ?? "",
            optionC: question?.optionC ?? "",
        });
        setCorrectOption((question?.correctOption ?? "").trim().toLowerCase());
        setScreenOrder(question?.screenOrder != null ? String(question.screenOrder) : "");
        setSaving(false);
        setError("");
    }, [show, question]);

    const setValue = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");

        if (!values.question.trim()) return setError("Please enter the question.");
        if (!values.optionA.trim() || !values.optionB.trim()) return setError("Options A and B are required.");
        if (!["a", "b", "c"].includes(correctOption)) return setError("Please choose the correct answer.");
        if (correctOption === "c" && !values.optionC.trim()) {
            return setError("The correct answer is C, but option C is empty.");
        }

        const body = {
            ...values,
            correctOption,
            screenOrder: screenOrder === "" ? null : Number(screenOrder),
        };

        try {
            setSaving(true);
            const saved = isEdit
                ? await apiSendJson("PUT", `/admin/content/test-questions/${question.id}`, body)
                : await apiSendJson("POST", `/admin/content/tests/${testId}/questions`, body);
            onSaved(saved);
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} size="lg" centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title className="fs-5">{isEdit ? "Edit question" : "Add question"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="g-2 mb-3">
                        <Col md={9}>
                            <Form.Label className="small text-muted mb-1">Question</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={values.question}
                                onChange={(e) => setValue("question", e.target.value)}
                                disabled={saving}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small text-muted mb-1">Order</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                placeholder={isEdit ? "" : "Auto (last)"}
                                value={screenOrder}
                                onChange={(e) => setScreenOrder(e.target.value)}
                                disabled={saving}
                            />
                        </Col>
                    </Row>

                    {OPTIONS.map((option) => (
                        <Form.Group key={option.letter} className="mb-2">
                            <Form.Label className="small text-muted mb-1">
                                Option {option.label}{option.letter === "c" && " (optional)"}
                            </Form.Label>
                            <Form.Control
                                value={values[option.field]}
                                onChange={(e) => setValue(option.field, e.target.value)}
                                isValid={correctOption === option.letter && Boolean(values[option.field].trim())}
                                disabled={saving}
                            />
                        </Form.Group>
                    ))}

                    <div className="mt-3">
                        <div className="small text-muted mb-1">Correct answer</div>
                        <ButtonGroup>
                            {OPTIONS.map((option) => (
                                <ToggleButton
                                    key={option.letter}
                                    id={`correct-option-${option.letter}`}
                                    type="radio"
                                    name="correct-option"
                                    variant={correctOption === option.letter ? "success" : "outline-success"}
                                    value={option.letter}
                                    checked={correctOption === option.letter}
                                    onChange={(e) => setCorrectOption(e.currentTarget.value)}
                                    disabled={saving}
                                >
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ButtonGroup>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving}>{isEdit ? "Save changes" : "Add question"}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
